/**
 * Services Sales List Page
 * Lists all service sales and budgets with filtering and search capabilities
 * Enhanced with modal-based sales creation
 */

import React, { useState } from 'react';
import { MainLayout } from '@/components/MainLayout';
import { SEO } from '@/components/SEO';
import { RouteDisplay } from '@/components/navigation/RouteDisplay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CalendarIcon, Filter, Plus, Search, MoreHorizontal, Eye, Edit, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

import { useServiceSales, useServiceSalesSummary } from '@/hooks/useServiceSales';
import { useAuth } from '@/hooks/useAuth';
import { formatCurrency, SERVICE_SALE_TYPE_LABELS, SERVICE_SALE_STATUS_LABELS, SERVICE_SALE_STATUS_COLORS } from '@/types/serviceSales';
import type { ServiceSaleFilters } from '@/types/serviceSales';

import { SalesModalRoute } from '@/components/modals/services/SalesModalRoute';

export default function SalesListPage() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<ServiceSaleFilters>({
    type: undefined,
    status: undefined,
  });

  // Fetch sales data
  const { data: sales, isLoading, error } = useServiceSales({
    ...filters,
    searchTerm: searchTerm || undefined,
  });

  const { data: summary } = useServiceSalesSummary(filters);

  const handleCreateNew = () => {
    // Check authentication
    if (authLoading) {
      toast.loading('Verificando autenticação...');
      return;
    }

    if (!user) {
      toast.error('Você precisa estar autenticado para criar uma nova venda.');
      const returnUrl = encodeURIComponent('/services/sales/new');
      navigate(`/login?returnUrl=${returnUrl}`);
      return;
    }

    // Navigate to modal route
    navigate('/services/sales/new');
  };

  const handleViewSale = (id: string) => {
    navigate(`/services/sales/${id}`);
  };

  const handleEditSale = (id: string) => {
    navigate(`/services/sales/${id}/edit`);
  };

  if (error) {
    toast.error('Erro ao carregar vendas de serviços');
  }

  return (
    <MainLayout>
      <SEO
        title="Vendas de Serviços"
        description="Gerencie suas vendas de serviços e orçamentos"
        keywords="vendas, serviços, orçamentos, gestão"
      />

      {/* Header */}
      <div className="section-delimited">
        <div className="flex items-center justify-between mb-6">
          <RouteDisplay
            showBreadcrumbs={true}
            showIcon={true}
            size="lg"
          />
          <Button onClick={handleCreateNew}>
            <Plus className="h-4 w-4 mr-2" />
            Nova Venda
          </Button>
        </div>
      </div>

      <div className="container mx-auto py-6 space-y-6">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary.total.count}</div>
                <p className="text-xs text-muted-foreground">
                  {formatCurrency(summary.total.value)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Previstas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary.forecasted.count}</div>
                <p className="text-xs text-muted-foreground">
                  {formatCurrency(summary.forecasted.value)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Aprovadas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary.approved.count}</div>
                <p className="text-xs text-muted-foreground">
                  {formatCurrency(summary.approved.value)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Canceladas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary.canceled.count}</div>
                <p className="text-xs text-muted-foreground">
                  {formatCurrency(summary.canceled.value)}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters and Search */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filtros
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Buscar por número, cliente..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Type Filter */}
              <Select
                value={filters.type?.[0] || 'all'}
                onValueChange={(value) => setFilters(prev => ({
                  ...prev,
                  type: value === 'all' ? undefined : [value as any]
                }))}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="sale">Vendas</SelectItem>
                  <SelectItem value="budget">Orçamentos</SelectItem>
                </SelectContent>
              </Select>

              {/* Status Filter */}
              <Select
                value={filters.status?.[0] || 'all'}
                onValueChange={(value) => setFilters(prev => ({
                  ...prev,
                  status: value === 'all' ? undefined : [value as any]
                }))}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="draft">Rascunho</SelectItem>
                  <SelectItem value="sent">Enviado</SelectItem>
                  <SelectItem value="approved">Aprovado</SelectItem>
                  <SelectItem value="rejected">Rejeitado</SelectItem>
                  <SelectItem value="expired">Expirado</SelectItem>
                  <SelectItem value="converted">Convertido</SelectItem>
                  <SelectItem value="cancelled">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Sales Table */}
        <Card>
          <CardHeader>
            <CardTitle>Vendas de Serviços</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Número</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      Carregando...
                    </TableCell>
                  </TableRow>
                ) : !sales || sales.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      Nenhuma venda encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  sales.map((sale) => (
                    <TableRow key={sale.id} className="cursor-pointer hover:bg-muted/50">
                      <TableCell className="font-medium">
                        {sale.number || `#${sale.id.slice(-6)}`}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {SERVICE_SALE_TYPE_LABELS[sale.type]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {sale.client?.name || 'Cliente não informado'}
                      </TableCell>
                      <TableCell>
                        {format(new Date(sale.sale_date || sale.created_at || ''), 'dd/MM/yyyy', {
                          locale: ptBR,
                        })}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(sale.total_value || 0)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={cn(SERVICE_SALE_STATUS_COLORS[sale.status || 'draft'])}
                        >
                          {SERVICE_SALE_STATUS_LABELS[sale.status || 'draft']}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewSale(sale.id)}>
                              <Eye className="h-4 w-4 mr-2" />
                              Visualizar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditSale(sale.id)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => {
                                // TODO: Implement delete functionality
                                toast.error('Funcionalidade não implementada');
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Modal Route for Deep Linking */}
      <SalesModalRoute />
    </MainLayout>
  );
}